import React, { useEffect, useRef } from "react";
import Layout from "../../components/layout/layout";
import Button from "../../components/library/button";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ExploreIcon } from "../../assets/images/svgExports";
import { solutionsData } from "../../utils/dataHelpers/solutions";

const SolutionsPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: reduce)", () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
      gsap.globalTimeline.clear();
    });

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const ctx = gsap.context(() => {
        gsap
          .timeline({ defaults: { ease: "power3.out", duration: 0.8 } })
          .from(".solutions-hero-heading", { y: 22, opacity: 0 })
          .from(".solutions-hero-heading p", { y: 16, opacity: 0 }, "-=0.45")
          .from(
            ".solutions-hero-heading a",
            { y: 10, opacity: 0, stagger: 0.08 },
            "-=0.45",
          );

        const sections = gsap.utils.toArray<HTMLElement>("[data-anim]");
        sections.forEach((el) => {
          gsap.fromTo(
            el,
            { y: 28, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.9,
              ease: "power3.out",
              scrollTrigger: {
                trigger: el,
                start: "top 80%",
                toggleActions: "play none none reverse",
              },
            },
          );
        });

        // Nice list stagger for any UL/OL marked data-stagger="list"
        const lists = gsap.utils.toArray<HTMLElement>('[data-stagger="list"]');
        lists.forEach((listEl) => {
          const items = Array.from(listEl.querySelectorAll("li"));
          if (!items.length) return;

          gsap.set(items, { willChange: "transform, opacity" });

          gsap.from(items, {
            opacity: 0,
            y: 18,
            scale: 0.985,
            duration: 0.65,
            ease: "power2.out",
            stagger: { each: 0.08, from: "start" },
            scrollTrigger: {
              trigger: listEl,
              start: "top 82%",
              toggleActions: "play none none reverse",
            },
            clearProps: "transform",
          });
        });

        // Cards batch
        const cardContainers = gsap.utils.toArray<HTMLElement>(
          '[data-stagger="cards"]',
        );
        cardContainers.forEach((wrap) => {
          const items = Array.from(wrap.children) as HTMLElement[];
          if (!items.length) return;

          ScrollTrigger.batch(items, {
            start: "top 85%",
            onEnter: (batch) =>
              gsap.fromTo(
                batch,
                { opacity: 0, y: 22, scale: 0.98 },
                {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  duration: 0.7,
                  ease: "power3.out",
                  stagger: 0.08,
                  overwrite: true,
                },
              ),
            onLeaveBack: (batch) =>
              gsap.to(batch, {
                opacity: 0,
                y: 22,
                scale: 0.98,
                duration: 0.35,
                ease: "power2.out",
                overwrite: true,
              }),
          });
        });

        // Stats batch
        const statContainers = gsap.utils.toArray<HTMLElement>(
          '[data-stagger="stats"]',
        );
        statContainers.forEach((wrap) => {
          const items = Array.from(wrap.children) as HTMLElement[];
          if (!items.length) return;

          ScrollTrigger.batch(items, {
            start: "top 85%",
            onEnter: (batch) =>
              gsap.fromTo(
                batch,
                { opacity: 0, y: 18 },
                {
                  opacity: 1,
                  y: 0,
                  duration: 0.7,
                  ease: "power3.out",
                  stagger: 0.1,
                  overwrite: true,
                },
              ),
            onLeaveBack: (batch) =>
              gsap.to(batch, {
                opacity: 0,
                y: 18,
                duration: 0.3,
                ease: "power2.out",
                overwrite: true,
              }),
          });
        });
      }, pageRef);

      return () => ctx.revert();
    });

    return () => mm.revert();
  }, []);

  return (
    <Layout
      footerData={{
        headerText: "Deploy Our Platform in Your Stack",
        bodyText:
          "Start integrating Bridgetune's APIs today. Full technical documentation, sandbox environment, and dedicated integration support included.",
      }}
    >
      <div ref={pageRef} className="bg-white mb-48">
        {/* HERO + MISSION */}
        <section
          className="
            relative bg-[#2C73DB] mb-0 md:mb-12
            rounded-bl-[80px] rounded-br-[80px]
            md:rounded-bl-[160px] md:rounded-br-[160px]
            lg:rounded-bl-[200px] lg:rounded-br-[200px]
            text-white pt-32 flex items-end justify-center
          "
        >
          <div className="max-w-5xl relative px-4 sm:px-6">
            <div className="solutions-hero-heading text-center mb-[5%]">
              <div className="inline-flex items-center bg-[#ffffff33] text-xs font-[300] py-2 px-3 rounded-full mb-3 border border-[#fff] shadow-[0_0_18px_rgba(59,130,246,0.25)]">
                Our Solution
              </div>
              <h1 className="text-3xl lg:text-4xl font-medium leading-tight mb-4 px-2">
                Engage, Retain & Reward Your Customers
              </h1>
              <p className="text-sm md:text-base text-blue-100 max-w-2xl mx-auto leading-relaxed">
                Powered by cutting-edge technology, our platform helps brands
                across Africa build lasting customer relationships through
                personalized engagement, smart retention strategies, and
                rewarding loyalty programs.
              </p>
              <Button className="inline-flex mt-8 items-center gap-2 w-3/4 md:w-auto bg-white text-[#2C73DB] hover:bg-blue-50 px-6 text-sm">
                Get Started
                <ExploreIcon />
              </Button>
            </div>
          </div>
        </section>

        <section className="mb-3 md:mb-56">
          {solutionsData.map((group, idx) => (
            <div
              key={idx}
              className={`space-y-4 md:space-y-8 w-[100%] max-w-7xl px-4 py-12 md:py-16 rounded-[40px] md:rounded-[60px] mx-auto ${idx % 2 ? "bg-[#F0F6FF]" : "bg-[#fff]"}`}
              id={group.id}
            >
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-medium text-[#1e1e1e]">
                  {group.sectionTitle}
                </h2>
              </div>

              <div
                className={`space-y-6 flex ${idx % 2 ? " flex-col md:flex-row-reverse" : "flex-col md:flex-row"} items-center justify-center`}
              >
                <div className={`overflow-hidden w-full md:w-[60%]  `}>
                  <img
                    src={group.heroImage}
                    alt={group.sectionTitle}
                    className={`w-full object-cover`}
                    loading="lazy"
                  />
                </div>
                <div
                  className={`w-full md:w-[35%] flex flex-col items-center justify-between p-4 rounded-[28px]  ${idx % 2 ? "bg-[#fff]" : "bg-[#F0F6FF]"} py-8 px-4 md:px-12 ${idx % 2 ? "md:translate-x-[35px]" : "md:-translate-x-[35px]"}`}
                >
                  {group.icon}
                  <p className="mt-3 text-xs md:text-sm leading-relaxed md:leading-6 text-[#8e8e8e] text-center">
                    {group.sectionSubtitle}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </section>
      </div>
    </Layout>
  );
};

export default SolutionsPage;
